<script lang="ts">
  import { getContext, onMount, createEventDispatcher } from "svelte";
  import { createPopperActions } from "svelte-popperjs";
  import { clickOutside } from "$actions/clickOutside";
  import { portal } from "$actions/portal.svelte";
  import ColorPicker from "$components/form/Fields/ColorField/ColorPicker.svelte";
  import WrapField from "$components/form/Fields/WrapField.svelte";
  import cx from "classnames";
  import type { createFormStore } from "$components/form/store";

  export let name: string;
  export let label = "";
  export let labelHidden = false;
  export let validationRules: Record<string, any> = {
    pattern: {
      value: /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/,
      message: "Invalid color: use a valid 3 or 6 digit hex code",
    },
  };
  export let size = "size-[36px]";
  export let wrapperClassname = "";
  export let value = "#6776FF";

  let open: boolean = false;
  let ref: HTMLDivElement;

  const formStore: ReturnType<typeof createFormStore> | undefined = getContext("form");
  const dispatch = createEventDispatcher();

  const [popperRef, popperContent] = createPopperActions({
    placement: "bottom-start",
    strategy: "fixed",
  });

  function onSelect() {
    if (formStore) {
      formStore.setField(name, value);
    } else {
      dispatch("change", value);
    }
    open = false;
  }

  onMount(() => {
    if (formStore) {
      formStore.addRef(name, ref);
      value = $formStore?.values[name];
    }
  });
</script>

<WrapField {label} {name} {wrapperClassname} {validationRules} {labelHidden}>
  <div
    class={cx(
      "shrink-0 rounded-lg border p-[3px] transition-[border-color,box-shadow] hover:shadow-sm",
      { "border-black/30 dark:border-white/30": open },
      { "border-black/10 dark:border-white/15": !open },
      size
    )}
    bind:this={ref}
  >
    <button
      type="button"
      style:background={value}
      class={cx(
        "transition-color flex size-full shrink-0 items-center justify-center rounded border duration-300 hover:border-black/40 hover:dark:border-white/40",
        { "border-black/40 dark:border-white/40": open },
        { "border-black/10 dark:border-white/10": !open }
      )}
      use:popperRef
      on:click={() => (open = !open)}
    />

    {#if open}
      <div
        use:popperContent={{
          modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
        }}
        use:portal={{ target: "body", hasBackLayer: true }}
        use:clickOutside={onSelect}
      >
        <ColorPicker bind:color={value} on:select={onSelect} />
      </div>
    {/if}
  </div>
</WrapField>
